import { Stack, Typography } from "@mui/material";
import { AquariusButton } from "@smartrr/shared/customerPortal/components/AquariusButton";
import { ConfirmCancelButtons } from "@smartrr/shared/customerPortal/components/ConfirmCancelButtons";
import { DotSpinner } from "@smartrr/shared/customerPortal/components/DotSpinner";
import { IPurchaseState } from "@smartrr/shared/entities/PurchaseState";
import { ScheduledDeliveryGroupWithPurchSt } from "@smartrr/shared/entities/PurchaseState/scheduleUtils";
import { ISmartrrSellingPlan } from "@smartrr/shared/entities/SellingPlanGroup";
import { ensureShopifyGid, shopifyGidToNumber } from "@smartrr/shared/utils/ensureShopifyGid";
import { typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import React, { useCallback, useState } from "react";

// Helpers & Utils
import { toast } from "react-toastify";

import { typedCustomerFrontendApi } from "@customer-app/shared/typedCustomerFrontendApi";
import { updateLocalCustomerPurchaseState } from "@customer-app/state/actionCreators/customerPurchaseStates";
import { useCloseModal } from "@customer-app/state/actionCreators/modal";
import { useSmartrrCustomerDispatch } from "@customer-app/state/typedCustomerReduxHooks";

import { CenterText } from "./CenterText";
import { ValidViews } from "../OrderOptionsModalInner";

interface ISwapPlanViewProps {
  delivery: ScheduledDeliveryGroupWithPurchSt<IPurchaseState>;
  setView: React.Dispatch<React.SetStateAction<ValidViews>>;
  sellingPlanOptions: ISmartrrSellingPlan[];
}

export const SwapPlanView = ({ delivery, setView, sellingPlanOptions }: ISwapPlanViewProps): JSX.Element => {
  const { purchaseState } = delivery;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [active, setActive] = useState<number>();
  const dispatch = useSmartrrCustomerDispatch();
  const closeModal = useCloseModal();
  const sellingPlanIdNumber = purchaseState.sellingPlanId ? shopifyGidToNumber(purchaseState.sellingPlanId) : 0;

  const onPlanChange = useCallback(
    async (sellingPlanId: number) => {
      setIsLoading(true);

      try {
        const res = await typedCustomerFrontendApi.putReq("/purchase-state/:id/selling-plan", {
          params: {
            id: purchaseState.id,
          },
          reqBody: {
            sellingPlanId: ensureShopifyGid("SellingPlan", sellingPlanId),
          },
        });

        if (res.type === "error") {
          throw new Error(res.message);
        }

        dispatch(updateLocalCustomerPurchaseState(res.body));
        dispatch({
          type: "PURCHASE_STATE_UPDATE_SELLING_PLAN_SUCCESS",
          payload: {
            purchaseState: res.body,
          },
        });
      } catch {
        toast.error(`There was an error updating frequency for subscription #${sellingPlanIdNumber}.`);
      } finally {
        setIsLoading(false);
        toast(typedLocalize("subscriptions.yourSubscriptions.orderOptionsModal.subPlanChangedToast"));
        closeModal();
      }
    },
    [purchaseState, isLoading, sellingPlanIdNumber, sellingPlanIdNumber]
  );

  return (
    <React.Fragment>
      {isLoading ? (
        <Stack justifyContent="center" height="240px" alignItems="center">
          <DotSpinner />
        </Stack>
      ) : (
        <CenterText
          title={typedLocalize("subscriptions.yourSubscriptions.orderOptionsModal.selectNewPlan")}
          sx={{
            paddingBottom: "0px !important",
            "& p": {
              width: "100%",
            },
          }}
        >
          <Stack direction="column" spacing="15px" justifyContent="center">
            {sellingPlanOptions.length ? (
              sellingPlanOptions.map((plan, i) => {
                if (sellingPlanIdNumber !== plan.shopifyNumericId!) {
                  return (
                    <AquariusButton
                      key={i}
                      onClick={() => setActive(shopifyGidToNumber(plan.shopifyNumericId!))}
                      selected={active === shopifyGidToNumber(plan.shopifyNumericId!)}
                    >
                      {plan.name}
                    </AquariusButton>
                  );
                }
              })
            ) : (
              <Typography variant="body1" mb={"20px"}>
                {typedLocalize("subscriptions.yourSubscriptions.orderOptionsModal.noPlans")}
              </Typography>
            )}
          </Stack>
        </CenterText>
      )}
      <ConfirmCancelButtons
        onCancel={() => setView("Default")}
        onConfirm={() => onPlanChange(active!)}
        showConfirmButton={!!active}
        isLoading={isLoading}
      />
    </React.Fragment>
  );
};
